import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { firebase } from '../../../services/firebase';
import { collection, doc, getDoc, onSnapshot, updateDoc, query, orderBy } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { ConfirmDialog } from "primereact/confirmdialog";
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ChatHeader } from './components/ChatHeader';
import { ChatActions } from './components/ChatActions';
import { ChatHistory } from './components/ChatHistory';
import { ChatJourney } from './components/ChatJourney';
import { ChatWrapper } from './components/ChatWrapper';
import * as CHAT_CONST from './constant';
import { useChatState } from './hooks/useChatState';
import { useChatJourney } from './hooks/useChatJourney';
import { useKeyboardVisibility } from '../../../hooks/useKeyboardVisibility';
import { logEvent, logError } from '../../../utils/analytics';
import { useResponsiveLayout } from '../../../utils/hooks/useResponsiveLayout';
import './style.css';

export const Chat = (props) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsiveLayout();
  const {
    user,
    userRecord,
    isCheckingUser,
    handleBackToChatList,
    onBack,
  } = props;
  const [isOwner, setIsOwner] = useState(props.isOwner || false);

  const [showNameInput, setShowNameInput] = useState(!localStorage.getItem("shopperName") && !props.isOwner);
  const [shopperName, setShopperName] = useState(localStorage.getItem("shopperName") || "");
  // Use constants for all magic numbers/strings
  // MAX_NAME_LENGTH is imported from CHAT_CONST
  const [shopDetails, setShopDetails] = useState(props.shopDetails || null);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [newName, setNewName] = useState(shopperName);
  const [uniqueId, setUniqueId] = useState(localStorage.getItem("uniqueId"));
  const MAX_NAME_LENGTH = 15;
  const isKeyboardVisible = useKeyboardVisibility();
  const chatHistoryRef = useRef(null);
  // Extract userId from either URL format (with or without business name)
  const userId = props.userId || searchParams.get("user");
  const guestParamId = isOwner ? props.guestId : searchParams.get("guestId");
  // Extract business name for SEO analytics
  const [guestId, setGuestId] = useState(guestParamId);

  const senderName = isOwner ? (shopDetails?.shopName || user?.displayName || user?.email) : shopperName;

  const { state, dispatch, sendMessage, buzzMessage, handleNameChange } = useChatState(userId, guestId);

  const { handleJourneyChoice, handleJourneyBack, handleTalkToShopOwner } = useChatJourney(userId, guestId, isOwner, dispatch, state, shopDetails, senderName);




  // Initialize guest ID
  useEffect(() => {
    const utcEpochTime = Date.now();
    let storedGuestId = localStorage.getItem("guestId");

    if (!storedGuestId) {
      storedGuestId = `${utcEpochTime}-${uuidv4()}`;
      localStorage.setItem("guestId", storedGuestId);
    }

    // Always set guestId based on the stored value for non-owners
    if (!isOwner) {
      setGuestId(storedGuestId);
    } else {
      setGuestId(guestParamId);
    }
  }, [isOwner, guestParamId]);

  // Sync guestId state with props.guestId when it changes (for owners)
  useEffect(() => {
    if (isOwner && props.guestId && props.guestId !== guestId) {
      setGuestId(props.guestId);
    }
  }, [props.guestId, isOwner, guestId]);

  // Update owner status based on props
  useEffect(() => {
    setIsOwner(props.isOwner);
  }, [props.isOwner]);

  // Update showNameInput based on props and local storage
  useEffect(() => {
    setShowNameInput(!localStorage.getItem("shopperName") && !props.isOwner);
  }, [props.isOwner]);

  // Initialize unique ID
  useEffect(() => {
    if (!uniqueId) {
      const newUniqueId = uuidv4();
      localStorage.setItem("uniqueId", newUniqueId);
      setUniqueId(newUniqueId);
    }
  }, [uniqueId]);

  // Scroll to bottom when keyboard appears/disappears or when messages change
  useEffect(() => {
    if (chatHistoryRef.current) {
      setTimeout(() => {
        if (chatHistoryRef.current) {
          chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [isKeyboardVisible, state.messages]);

  // Listen for chat status changes
  useEffect(() => {
    if (!userId || !guestId) {
      return;
    }

    const guestChatRef = doc(firebase, `users/${userId}/chats/${guestId}`);

    const unsubscribeChatStatus = onSnapshot(guestChatRef, (chatDoc) => {
      if (chatDoc.exists()) {
        dispatch({ type: 'SET_CLOSED', payload: chatDoc.data().closed || false });
        dispatch({ type: 'SET_SELECTED_TAG', payload: chatDoc.data().tag || null });
        if (isOwner && chatDoc.data().shopperName) {
          setShopperName(chatDoc.data().shopperName);
        } else if (!isOwner) {
          // Set shopperName for non-owners from chat data
          const chatShopperName = chatDoc.data().shopperName;
          if (chatShopperName) {
            setShopperName(chatShopperName);
            localStorage.setItem("shopperName", chatShopperName);
            setShowNameInput(false);
          }
        }
      }
    }, (error) => {
      logError('chat_status_snapshot', error);
    });

    const messagesCollection = collection(firebase, `users/${userId}/chats/${guestId}/messages`);
    const q = query(messagesCollection, orderBy("timestamp"));

    const unsubscribeMessages = onSnapshot(q, (snapshot) => {
      const newMessages = snapshot.docs.map(doc => ({
        ...doc.data(),
        timestamp: doc.data().timestamp
      }));

      dispatch({ type: 'SET_MESSAGES', payload: newMessages });
    }, (error) => {
      logError('messages_snapshot', error);
    });

    return () => {
      unsubscribeChatStatus();
      unsubscribeMessages();
    };
  }, [userId, guestId, isOwner, state.showChatJourney, dispatch, props.userId, props.shopDetails]);

  // Show journey message when currentJourneyNode or shopName changes
 useEffect(() => {
    if (
      state.showChatJourney &&
      state.currentJourneyNode &&
      state.currentJourneyNode.data &&
      !state.messages.some(m => m.text === state.currentJourneyNode.data.question)
    ) {
      dispatch({
        type: 'SET_JOURNEY_MESSAGE',
        payload: {
          response: state.currentJourneyNode.data.question,
          sender: shopDetails?.shopName || 'Shop',
        },
      });
    }
    // Only depends on state.currentJourneyNode, state.messages, shopDetails?.shopName, showChatJourney
  }, [state.showChatJourney, state.currentJourneyNode, state.messages, shopDetails?.shopName, dispatch]);

  // Fetch shop details from userRecord
  useEffect(() => {
    if (!userId || !userRecord) return;

    try {
      setShopDetails({
        shopName: userRecord.shopName,
        storeImage: userRecord.storeImage,
        storeAddress: userRecord.storeAddress,
        directions: userRecord.directions,
        location: userRecord.location,
        shopType: userRecord.shopType
      });
    } catch (error) {
      logError('fetchShopDetails', error);
    }
  }, [userId, userRecord]);

  // Handle name submission
  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!shopperName.trim()) return;

    const trimmedName = shopperName.slice(0, MAX_NAME_LENGTH).trim();
    localStorage.setItem("shopperName", trimmedName);
    setShopperName(trimmedName);
    setShowNameInput(false);
    logEvent('nameSubmit', { shopperName: trimmedName });
  };

  // Handle name change
  const handleNameChangeSubmit = async (newName) => {
    if (!newName.trim()) return;

    const trimmedName = newName.trim();
    if (trimmedName === shopperName) return;

    // Set the new name and show the confirmation dialog
    setNewName(trimmedName);
    dispatch({ type: 'SET_SHOW_NAME_CHANGE_DIALOG', payload: true });
  };

  // Dialog footers
  const nameDialogFooter = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={() => setShowNameDialog(false)}
        className="p-button-text"
      />
      <Button
        label="Change Nickname"
        icon="pi pi-check"
        onClick={handleNameChangeSubmit}
        disabled={!newName.trim()}
        autoFocus
      />
    </div>
  );

  // Name change dialog footer
  const nameChangeDialogFooter = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={() => {
          dispatch({ type: 'SET_SHOW_NAME_CHANGE_DIALOG', payload: false });
          setShowNameDialog(false);
        }}
        className="p-button-text"
      />
      <Button
        label="Start New Chat"
        className="p-button p-button-primary"
        icon="pi pi-check"
        onClick={async () => {
          const trimmedName = newName.slice(0, MAX_NAME_LENGTH).trim();
          await handleNameChange(userId, guestId, uniqueId, trimmedName, navigate);
          setShopperName(trimmedName);
          dispatch({ type: 'SET_SHOW_NAME_CHANGE_DIALOG', payload: false });
          setShowNameDialog(false);
        }}
        autoFocus
      />
    </div>
  );

  const spamDialogFooter = (
    <div>
      <Button label="Understood" icon="pi pi-check" onClick={() => dispatch({ type: 'SET_SHOW_SPAM_DIALOG', payload: false })} autoFocus />
    </div>
  );

  const spamWarningDialogFooter = (
    <div>
      <Button label="Understood" icon="pi pi-check" onClick={async () => {
        dispatch({ type: 'SET_SHOW_SPAM_WARNING_DIALOG', payload: false });
        await buzzMessage(userId, guestId, senderName, uniqueId, isOwner);
      }} autoFocus />
    </div>
  );

  const blockedDialogFooter = (
    <div>
      <Button label="Understood" icon="pi pi-check" onClick={() => {
        dispatch({ type: 'SET_SHOW_BLOCKED_DIALOG', payload: false });
        navigate("/");
      }} autoFocus />
    </div>
  );

  const closeChatDialogFooter = (
    <div>
      <Button label="Understood" icon="pi pi-check" onClick={() => {
        dispatch({ type: 'SET_SHOW_CLOSE_CHAT_DIALOG', payload: false });
        closeChat();
      }} autoFocus />
    </div>
  );

  const blockUserDialogFooter = (
    <div>
      <Button label="Understood" icon="pi pi-check" onClick={() => {
        dispatch({ type: 'SET_SHOW_BLOCK_USER_DIALOG', payload: false });
        updateTagAndClose("Block", true);
      }} autoFocus />
    </div>
  );

  // Close chat
  const closeChat = async () => {
    if (!userId || !guestId) return;

    if (state.messages.length === 0) {
      navigate("/");
      return;
    }

    try {
      const chatRef = doc(firebase, `users/${userId}/chats/${guestId}`);
      await updateDoc(chatRef, { closed: true });
      dispatch({ type: 'SET_CLOSED', payload: true });
      logEvent('closeChat', { guestId });
      navigate("/");
    } catch (error) {
      logError('closeChat', error);
    }
  };

  // Update tag and close chat
  const updateTagAndClose = async (tag, closeChat) => {
    if (!userId || !guestId) return;

    try {
      const guestRef = doc(firebase, `users/${userId}/chats/${guestId}`);
      const chatDoc = await getDoc(guestRef);
      const chatData = chatDoc.data();

      await updateDoc(guestRef, {
        tag,
        closed: closeChat,
        uniqueId: chatData.uniqueId
      });

      dispatch({ type: 'SET_SELECTED_TAG', payload: tag });
      logEvent('updateTag', { tag, closeChat });

      if (!isOwner && closeChat) {
        dispatch({ type: 'SET_CLOSED', payload: true });
        navigate("/");
      }
    } catch (error) {
      logError('updateTagAndClose', error);
    }
  };

  // Confirm tag change
  const confirmTagChange = (tag) => {
    if (!isOwner) {
      dispatch({ type: 'SET_SHOW_CLOSE_CHAT_DIALOG', payload: true });
      return;
    }

    dispatch({ type: 'SET_SELECTED_TAG', payload: tag });

    if (tag !== "Block") {
      updateTagAndClose(tag, false);
      return;
    }

    dispatch({ type: 'SET_SHOW_BLOCK_USER_DIALOG', payload: true });
  };

  // Edit message
  const editMessage = async (messageId, newText) => {
    if (!userId || !guestId || state.isClosed) return;

    try {
      const messageRef = doc(firebase, `users/${userId}/chats/${guestId}/messages/${messageId}`);
      await updateDoc(messageRef, {
        text: newText,
        edited: true,
        editedAt: new Date()
      });
      dispatch({ type: 'SET_EDITING_MESSAGE', payload: null });
      logEvent('editMessage', { messageId });
    } catch (error) {
      logError('editMessage', error);
    }
  };

  // Handle sending messages (now supports {text, images})
  const handleSendMessage = (formattedMessage) => {
    // Accept both string (legacy) and {text, images} (new)
    if (!formattedMessage) return;
    if (typeof formattedMessage === 'object' && formattedMessage !== null) {
      const hasText = formattedMessage.text && formattedMessage.text.trim();
      const hasImages = Array.isArray(formattedMessage.images) && formattedMessage.images.length > 0;
      if (!hasText && !hasImages) return;
    } else if (typeof formattedMessage === 'string') {
      if (!formattedMessage.trim()) return;
    }
    sendMessage(userId, guestId, senderName, uniqueId, isOwner, shopperName, formattedMessage);
  };


  // Handle buzz message
  const handleBuzzMessage = () => {
    buzzMessage(userId, guestId, senderName, uniqueId, isOwner);
  };

  // Show loading state while checking user
  if (isCheckingUser) {
    return <div className="loading-container">{CHAT_CONST.LOADING_TEXT}</div>;
  }

  // Show name input if needed
  if (showNameInput && !isOwner) {
    return (
      <div className="name-input-container">
        <div className="name-input-card">
          <h2>{CHAT_CONST.NAME_INPUT_HEADER}</h2>
          <p>{CHAT_CONST.NAME_INPUT_DESCRIPTION}</p>
          <form onSubmit={handleNameSubmit}>
            <div className="name-input-field mb-4">
              <InputText
                value={shopperName}
                onChange={(e) => setShopperName(e.target.value.slice(0, MAX_NAME_LENGTH))}
                placeholder={CHAT_CONST.NAME_INPUT_PLACEHOLDER}
                className="w-100"
                maxLength={MAX_NAME_LENGTH}
                autoFocus
              />
            </div>
            <Button
              label={CHAT_CONST.START_CHATTING_LABEL}
              type="submit"
              className="p-button-primary w-100"
              disabled={!shopperName.trim()}
            />
          </form>
        </div>
      </div>
    );
  }

  // Main chat layout using ChatWrapper
  return (
    <>
      <ConfirmDialog />
      <ChatWrapper
        header={
          <ChatHeader
            isOwner={isOwner}
            shopperName={shopperName}
            setShopperName={setShopperName}
            shopDetails={shopDetails}
            isClosed={state.isClosed}
            confirmTagChange={confirmTagChange}
            onNameChange={handleNameChangeSubmit}
            selectedTag={state.selectedTag}
            isBionicReading={state.isBionicReading}
            onBionicReadingToggle={() => dispatch({ type: 'SET_BIONIC_READING', payload: !state.isBionicReading })}
            messages={state.messages}
            showChatJourney={state.showChatJourney}
            onBack={isMobile ? onBack || handleBackToChatList : undefined}
          />
        }
        history={
          <ChatHistory
            messages={state.messages}
            senderName={senderName}
            editingMessage={state.editingMessage}
            setEditingMessage={(id) => dispatch({ type: 'SET_EDITING_MESSAGE', payload: id })}
            editMessage={editMessage}
            isClosed={state.isClosed}
            shopperName={shopperName}
            isOwner={isOwner}
            selectedTag={state.selectedTag}
            shopName={shopDetails?.shopName}
            isBionicReading={state.isBionicReading}
            isSending={state.isSending}
            showChatJourney={state.showChatJourney}
          />
        }
        actions={
          <ChatActions
            isOwner={isOwner}
            newMessage={state.newMessage}
            setNewMessage={(value) => dispatch({ type: 'SET_NEW_MESSAGE', payload: value })}
            onSendMessage={handleSendMessage}
            isClosed={state.isClosed}
            buzzMessage={handleBuzzMessage}
            selectedTag={state.selectedTag}
            isSending={state.isSending}
            shopperName={shopperName}
            messages={state.messages}
            userId={userId}
            guestId={guestId}
          />
        }
        journey={(state.showChatJourney && state.chatJourney) ? (
          <ChatJourney
            currentJourneyNode={state.currentJourneyNode}
            parentJourneyNode={state.parentJourneyNode}
            chatJourney={state.chatJourney}
            onJourneyChoice={handleJourneyChoice}
            editMessage={editMessage}
            isClosed={state.isClosed}
            shopperName={shopperName}
            isOwner={isOwner}
            selectedTag={state.selectedTag}
            shopName={shopDetails?.shopName}
            isBionicReading={state.isBionicReading}
            isSending={state.isSending}
            showChatJourney={state.showChatJourney}
            onTalkToShopOwner={handleTalkToShopOwner}
            onJourneyBack={handleJourneyBack}
          />
        ) : null}
      />
      {/* Dialogs remain below for modals, outside main layout */}
      <Dialog
        visible={state.showSpamDialog}
        style={{ width: '450px' }}
        header={CHAT_CONST.SPAM_HEADER}
        modal
        footer={spamDialogFooter}
        onHide={() => dispatch({ type: 'SET_SHOW_SPAM_DIALOG', payload: false })}
        className="p-fluid"
      >
        <div className="confirmation-content">
          <i className={`${CHAT_CONST.PI_BAN_ICON} mr-3`} style={{ fontSize: '2rem' }} />
          <span>{CHAT_CONST.SPAM_DESCRIPTION}</span>
        </div>
      </Dialog>
      <Dialog
        visible={state.showSpamWarningDialog}
        style={{ width: '450px' }}
        header={CHAT_CONST.SPAM_WARNING_HEADER}
        modal
        footer={spamWarningDialogFooter}
        onHide={() => dispatch({ type: 'SET_SHOW_SPAM_WARNING_DIALOG', payload: false })}
        className="p-fluid"
      >
        <div className="confirmation-content">
          <i className={`${CHAT_CONST.PI_EXCLAMATION_TRIANGLE_ICON} mr-3`} style={{ fontSize: '2rem' }} />
          <span>{CHAT_CONST.SPAM_WARNING_DESCRIPTION}</span>
        </div>
      </Dialog>
      <Dialog
        visible={state.showBlockedDialog}
        style={{ width: '450px' }}
        header="Access Denied"
        modal
        footer={blockedDialogFooter}
        onHide={() => dispatch({ type: 'SET_SHOW_BLOCKED_DIALOG', payload: false })}
        className="p-fluid"
      >
        <div className="confirmation-content">
          <i className="pi pi-ban mr-3" style={{ fontSize: '2rem' }} />
          <span>You have been blocked from sending messages to this shop. This means you cannot send any more messages to this shop.</span>
        </div>
      </Dialog>

      {/* Close Chat Dialog */}
      <Dialog
        visible={state.showCloseChatDialog}
        style={{ width: '450px' }}
        header="Close Chat"
        modal
        footer={closeChatDialogFooter}
        onHide={() => dispatch({ type: 'SET_SHOW_CLOSE_CHAT_DIALOG', payload: false })}
        className="p-fluid"
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>Do you want to close the chat?</span>
        </div>
      </Dialog>

      {/* Block User Dialog */}
      <Dialog
        visible={state.showBlockUserDialog}
        style={{ width: '450px' }}
        header="Block user"
        modal
        footer={blockUserDialogFooter}
        onHide={() => {
          dispatch({ type: 'SET_SHOW_BLOCK_USER_DIALOG', payload: false });
          dispatch({ type: 'SET_SELECTED_TAG', payload: state.selectedTag === "Block" ? null : state.selectedTag });
        }}
        className="p-fluid"
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>Do you want to block this user?</span>
        </div>
      </Dialog>

      {/* Name Change Dialog */}
      <Dialog
        visible={state.showNameChangeDialog}
        style={{ width: '450px' }}
        header="Start New Chat?"
        modal
        footer={nameChangeDialogFooter}
        onHide={() => {
          dispatch({ type: 'SET_SHOW_NAME_CHANGE_DIALOG', payload: false });
          setShowNameDialog(false);
        }}
        className="p-fluid"
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>Changing your nickname will start a new chat. Do you want to proceed?</span>
        </div>
      </Dialog>
    </>
  );
};

